import { getLocationById } from 'Global/JourneyElements/Utils/getLocationById';
import React from 'react';

import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { useOrganisationStore } from 'Global/Organisations/OrganisationsStore';
import { OrganisationEntity } from 'Models/Entities';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { handleAppError } from 'Validators/AppError';
import { useBookingWizardDataStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import {
	//
	Button,
	Colors,
	Display,
	Sizes,
} from 'Views/Components/Button/Button';
import { Combobox } from 'Views/Components/Combobox/Combobox';
import { useIntl } from 'react-intl';
import {
	isTravelPossible,
	processTripSelectorState,
	TripSelectorActionEvent,
	TripSelectorActionType,
	tripSelectorReducer,
} from './Reducers/tripSelectorReducer';

interface TripSelectorProps {
	startLocationId: string;
	endLocationId: string;
	setLocations(state: { startLocationId: string; endLocationId: string }): void;
	disabled?: boolean;
	/**
	 * Optional `data-test-id` applied to the trip-direction selector wrapper for
	 * e2e targeting. From/To comboboxes are reachable via the `.departure` and
	 * `.destination` class hooks scoped under this id.
	 */
	'data-test-id'?: string;
}

export default function TripSelector(props: TripSelectorProps) {
	const isLoading = journeyElementsStore(x => x.isLoading);
	const selectedOrg = useOrganisationStore(x => x.selectedOrganisation);
	if (isLoading) {
		return <></>;
	}
	return <Body {...props} selectedOrg={selectedOrg} />;
}

interface BodyProps extends TripSelectorProps {
	selectedOrg: OrganisationEntity | null;
}

function Body({
	selectedOrg,
	startLocationId: startLocationIdParam,
	endLocationId: endLocationIdParam,
	setLocations,
	disabled,
	'data-test-id': testId,
}: BodyProps) {
	const intl = useIntl();
	const journeyElements = journeyElementsStore();

	const { wizardData } = useBookingWizardDataStore(x => ({
		wizardData: x.wizardData,
	}));

	const [multiOrgCheck, setMultiOrgCheck] = React.useState(false);
	const [state, dispatch] = React.useReducer(
		tripSelectorReducer,
		processTripSelectorState(
			journeyElements,
			startLocationIdParam,
			endLocationIdParam,
			wizardData.wizardMode === 'ALTERATION' ? null : selectedOrg?.id,
		),
	);

	React.useEffect(() => {
		const res = processTripSelectorState(
			journeyElements,
			startLocationIdParam,
			endLocationIdParam,
			wizardData.wizardMode === 'ALTERATION' ? null : selectedOrg?.id,
		);
		if (res.startLocationId !== state.startLocationId || res.endLocationId !== state.endLocationId) {
			dispatch({
				type: TripSelectorActionType.ChangeStartLocation,
				journeyElements,
				orgId: selectedOrg?.id,
				startLocationId: '',
			});
		}
	}, [selectedOrg?.id]);

	const {
		//
		startLocationIds,
		startLocationId,
		endLocationId,
		possibleDestinationIds,
	} = state;

	const dispatchWithErrorHandling = (event: TripSelectorActionEvent) => {
		try {
			dispatch(event);
		} catch (error) {
			handleAppError(error);
			if (process.env.NODE_ENV === 'development') {
				throw error; // Rethrow the error for debugging.
			}
		}
	};

	React.useEffect(() => {
		// Sync state with external setLocations function whenever start or end location changes
		setLocations({ startLocationId, endLocationId });
	}, [startLocationId, endLocationId]);

	const departureOptions = startLocationIds
		.map(id => getLocationById(id))
		.sort((a, b) => (isNullOrUndefined(a) || isNullOrUndefined(b) ? 0 : a.name.localeCompare(b.name)))
		.filter(location => !!location)
		.map(location => ({
			display: location?.name,
			value: location?.id,
		}));

	const destinationOptions = possibleDestinationIds
		.map(id => getLocationById(id))
		.sort((a, b) => (isNullOrUndefined(a) || isNullOrUndefined(b) ? 0 : a.name.localeCompare(b.name)))
		.filter(location => !!location)
		.map(location => ({
			display: location?.name,
			value: location?.id,
		}));

	return (
		<div className="trip-direction-selector__container" data-test-id={testId}>
			<Combobox
				className="custom-dropdown departure"
				model={{ startLocationId }}
				modelProperty="startLocationId"
				label={intl.formatMessage({
					id: 'trip-direction-selector__from-input__label',
					defaultMessage: 'From',
				})}
				options={departureOptions}
				searchable={false}
				onChange={(_, data) => {
					dispatchWithErrorHandling({
						journeyElements,
						type: TripSelectorActionType.ChangeStartLocation,
						startLocationId: data.value as string,
						orgId: selectedOrg?.id,
					});
				}}
				isDisabled={disabled}
			/>
			<div className="flip-direction-button__container" data-test-id="flip-direction-button">
				<Button
					className="flip-direction-button"
					display={Display.Text}
					colors={Colors.None}
					sizes={Sizes.ExtraLarge}
					onClick={() => {
						dispatchWithErrorHandling({
							type: TripSelectorActionType.SwitchLocations,
							journeyElements,
							orgId: selectedOrg?.id,
						});
					}}
					disabled={
						!isTravelPossible(journeyElements, startLocationId, endLocationId, selectedOrg?.id) || disabled
					}
				>
					<Icon name="sync_alt" useMaterialIcons />
				</Button>
			</div>
			<Combobox
				className="custom-dropdown destination"
				model={{ endLocationId }}
				modelProperty="endLocationId"
				label={intl.formatMessage({
					id: 'trip-direction-selector__to-input__label',
					defaultMessage: 'To',
				})}
				options={destinationOptions}
				searchable={false}
				isDisabled={destinationOptions.length < 2 || disabled}
				onChange={(_, data) => {
					dispatchWithErrorHandling({
						type: TripSelectorActionType.ChangeEndLocation,
						endLocationId: data.value as string,
					});
				}}
			/>
		</div>
	);
}
